import React, { type ReactNode, type SelectHTMLAttributes, useMemo, useRef, useState } from 'react'
import { useSelect, useMultipleSelection, useCombobox } from 'downshift'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { ContextMenu } from '@mobi/component-library/Overlays/ContextMenu'
import { Menu, MenuItem } from '@mobi/component-library/Navigation/Menu/Menu'
import type { SharedProps } from '../Inputs/types'
import { Checkbox } from '../Inputs/Checkbox'
import {
  DropdownInputStyled,
  DropdownTrigger,
  DropdownChevron,
  IconWrapper,
  Divider,
  OptionContent,
  OptionLabel,
  OptionSubText,
  SearchInputWrapper,
  SearchInput,
  ClearButton,
} from './DropdownInput.styled'

// ======================
// Types
// ======================

export type DropdownOption = {
  value: string
  label: string
  subText?: string
  hasDivider?: boolean
}

type BaseProps = SharedProps & {
  /**
   * A string or React node to use as the prefix.
   */
  prefix?: ReactNode
  /**
   * The placeholder text for the dropdown input.
   * Only shows when the dropdown input is empty.
   */
  placeholder?: string
  /**
   * Whether the value of the dropdown input is invalid.
   */
  isInvalid?: boolean
}

/** @deprecated Use SingleSelectProps or MultiSelectProps instead */
type LegacyProps = BaseProps & {
  options?: never
  multiple?: never
  searchable?: never
  /**
   * The value of the dropdown input.
   */
  value?: string
  /**
   * Children to render as options (legacy native select)
   */
  children: ReactNode
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'prefix' | 'disabled'>

type SingleSelectProps = BaseProps & {
  /**
   * Whether multiple options can be selected
   */
  multiple?: false
  /**
   * Whether the dropdown is searchable
   */
  searchable?: boolean
  /**
   * The options for the dropdown. When provided, uses custom non-native dropdown.
   */
  options: DropdownOption[]
  /**
   * The selected value (for single select)
   */
  value?: string
  /**
   * Callback when selection changes (for single select)
   */
  onChange?: (value: string) => void
  /**
   * Callback when the dropdown loses focus
   */
  onBlur?: () => void
  children?: never
}

type MultiSelectProps = BaseProps & {
  /**
   * Whether multiple options can be selected
   */
  multiple: true
  /**
   * Whether the dropdown is searchable (not supported for multi-select)
   */
  searchable?: never
  /**
   * The options for the dropdown.
   */
  options: DropdownOption[]
  /**
   * The selected values (for multiple select)
   */
  value?: string[]
  /**
   * Callback when selection changes (for multiple select)
   */
  onChange?: (values: string[]) => void
  /**
   * Callback when the dropdown loses focus
   */
  onBlur?: () => void
  children?: never
}

type Props = LegacyProps | SingleSelectProps | MultiSelectProps

// ======================
// Custom Hooks
// ======================

const useSelectedItem = (options: DropdownOption[], value?: string) =>
  useMemo(() => options.find(opt => opt.value === value) ?? null, [options, value])

const useSelectedItems = (options: DropdownOption[], values: string[]) =>
  useMemo(() => options.filter(opt => values.includes(opt.value)), [options, values])

// ======================
// Shared Components
// ======================

type DropdownWrapperProps = {
  className?: string
  label?: ReactNode
  helpText?: ReactNode
  isInvalid: boolean
  isDisabled: boolean
  isPlaceholder: boolean
  isOpen: boolean
  isLegacyDropdown?: boolean
  labelProps?: React.HTMLAttributes<HTMLLabelElement>
  children: ReactNode
}

const DropdownWrapper = ({
  className,
  label,
  helpText,
  isInvalid,
  isDisabled,
  isPlaceholder,
  isOpen,
  isLegacyDropdown,
  labelProps,
  children,
}: DropdownWrapperProps) => (
  <DropdownInputStyled
    className={className}
    invalid={isInvalid}
    disabled={isDisabled}
    isPlaceholderSelected={isPlaceholder}
    isOpen={isOpen}
    isLegacyDropdown={isLegacyDropdown}
  >
    {label && <label {...labelProps}>{label}</label>}
    {children}
    <span>{helpText}</span>
  </DropdownInputStyled>
)

type DropdownTriggerButtonProps = {
  triggerRef: React.RefObject<HTMLButtonElement>
  displayText: ReactNode
  isDisabled: boolean
  isInvalid: boolean
  isPlaceholder: boolean
  isOpen: boolean
  buttonProps: React.ButtonHTMLAttributes<HTMLButtonElement>
}

const DropdownTriggerButton = ({
  triggerRef,
  displayText,
  isDisabled,
  isInvalid,
  isPlaceholder,
  isOpen,
  buttonProps,
}: DropdownTriggerButtonProps) => (
  <span ref={triggerRef}>
    <DropdownTrigger
      type='button'
      {...buttonProps}
      disabled={isDisabled}
      invalid={isInvalid}
      isPlaceholderSelected={isPlaceholder}
      isOpen={isOpen}
    >
      <span>{displayText}</span>
      <IconWrapper $rotate={isOpen}>
        <Icon name='SolidChevronDown' size='1.6rem' />
      </IconWrapper>
    </DropdownTrigger>
  </span>
)

type DropdownMenuProps = {
  isOpen: boolean
  triggerRef: React.RefObject<HTMLButtonElement | HTMLDivElement>
  menuProps: React.HTMLAttributes<HTMLUListElement>
  options: DropdownOption[]
  renderItem: (option: DropdownOption, index: number) => ReactNode
}

const DropdownMenu = ({
  isOpen,
  triggerRef,
  menuProps,
  options,
  renderItem,
}: DropdownMenuProps) => (
  <ContextMenu
    isOpen={isOpen}
    referenceElRef={triggerRef}
    placement='bottom'
    offsetYPx={8}
    handleDismiss={() => undefined}
    fullWidth
    positionStrategy='absolute'
  >
    <div style={{ width: '100%', maxHeight: '330px', overflowY: 'auto' }}>
      <Menu {...menuProps}>
        {options.map((option, index) => (
          <React.Fragment key={option.value}>
            {renderItem(option, index)}
            {option.hasDivider && index < options.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Menu>
    </div>
  </ContextMenu>
)

type StandardMenuItemProps = {
  option: DropdownOption
  itemProps: React.HTMLAttributes<HTMLElement>
  isSelected: boolean
  isHighlighted: boolean
}

const StandardMenuItem = ({
  option,
  itemProps,
  isSelected,
  isHighlighted,
}: StandardMenuItemProps) => (
  <MenuItem {...itemProps} isSelected={isSelected} isHighlighted={isHighlighted}>
    <OptionContent>
      <OptionLabel>{option.label}</OptionLabel>
      {option.subText && <OptionSubText>{option.subText}</OptionSubText>}
    </OptionContent>
  </MenuItem>
)

// ======================
// Main Component
// ======================

export const DropdownInput = ({ id, name, options, multiple, ...restProps }: Props) => {
  const inputId = id || name

  if (!options) {
    return <LegacyDropdown {...(restProps as LegacyProps)} id={inputId} name={name} />
  }

  if (multiple) {
    return (
      <MultiSelectDropdown
        id={inputId}
        options={options}
        {...(restProps as Omit<MultiSelectProps, 'options' | 'multiple'>)}
      />
    )
  }

  const singleProps = restProps as Omit<SingleSelectProps, 'options'>
  const Component = singleProps.searchable ? SearchableDropdown : SingleSelectDropdown

  return <Component id={inputId} options={options} {...singleProps} />
}

DropdownInput.displayName = 'DropdownInput'

// ======================
// Legacy Native Dropdown
// ======================

/** @deprecated Use SingleSelectDropdown or MultiSelectDropdown instead */
const LegacyDropdown = ({
  id,
  name,
  className,
  label,
  helpText,
  prefix,
  isInvalid = false,
  isDisabled = false,
  children,
  placeholder,
  ...dropdownInputProps
}: LegacyProps) => (
  <DropdownInputStyled
    className={className}
    invalid={isInvalid}
    disabled={isDisabled}
    isPlaceholderSelected={placeholder != null && dropdownInputProps.value === ''}
    isLegacyDropdown
  >
    {label && <label htmlFor={id}>{label}</label>}
    <div>
      {typeof prefix === 'string' ? <span>{prefix}</span> : prefix}
      <select name={name} id={id} {...dropdownInputProps} disabled={isDisabled}>
        {placeholder && (
          <option value='' hidden>
            {placeholder}
          </option>
        )}
        {children}
      </select>
      <DropdownChevron>
        <Icon name='SolidChevronDown' size='1.6rem' />
      </DropdownChevron>
    </div>
    <span>{helpText}</span>
  </DropdownInputStyled>
)

// ======================
// Single Select Dropdown
// ======================

const SingleSelectDropdown = ({
  id,
  className,
  label,
  helpText,
  placeholder,
  options,
  isInvalid = false,
  isDisabled = false,
  value,
  onChange,
  onBlur,
}: Omit<SingleSelectProps, 'searchable'>) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const selectedItem = useSelectedItem(options, value)
  const isPlaceholder = !selectedItem

  const {
    isOpen,
    highlightedIndex,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
  } = useSelect({
    id,
    items: options,
    selectedItem,
    itemToString: item => item?.label ?? '',
    onSelectedItemChange: ({ selectedItem: newItem }) => onChange?.(newItem?.value ?? ''),
    onIsOpenChange: ({ isOpen: newIsOpen }) => {
      if (!newIsOpen) onBlur?.()
    },
  })

  return (
    <DropdownWrapper
      className={className}
      label={label}
      helpText={helpText}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      isPlaceholder={isPlaceholder}
      isOpen={isOpen}
      labelProps={getLabelProps()}
    >
      <DropdownTriggerButton
        triggerRef={triggerRef}
        displayText={selectedItem?.label || placeholder}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        isPlaceholder={isPlaceholder}
        isOpen={isOpen}
        buttonProps={getToggleButtonProps()}
      />
      <DropdownMenu
        isOpen={isOpen}
        triggerRef={triggerRef}
        menuProps={getMenuProps({}, { suppressRefError: true })}
        options={options}
        renderItem={(option, index) => (
          <StandardMenuItem
            option={option}
            itemProps={getItemProps({ item: option, index })}
            isSelected={selectedItem?.value === option.value}
            isHighlighted={highlightedIndex === index}
          />
        )}
      />
    </DropdownWrapper>
  )
}

// ======================
// Searchable Dropdown
// ======================

const SearchableDropdown = ({
  id,
  className,
  label,
  helpText,
  placeholder = 'Search...',
  options,
  isInvalid = false,
  isDisabled = false,
  value,
  onChange,
  onBlur,
}: Omit<SingleSelectProps, 'searchable'>) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState('')

  const selectedItem = useSelectedItem(options, value)

  const filteredOptions = useMemo(() => {
    if (!inputValue) return options
    const lowerInput = inputValue.toLowerCase()
    return options.filter(
      opt =>
        opt.label.toLowerCase().includes(lowerInput) ||
        opt.subText?.toLowerCase().includes(lowerInput)
    )
  }, [options, inputValue])

  const { isOpen, highlightedIndex, getInputProps, getLabelProps, getMenuProps, getItemProps } =
    useCombobox({
      id,
      items: filteredOptions,
      selectedItem,
      inputValue,
      itemToString: item => item?.label ?? '',
      onInputValueChange: ({ inputValue: newValue }) => setInputValue(newValue ?? ''),
      onSelectedItemChange: ({ selectedItem: newItem }) => {
        if (newItem) {
          inputRef.current?.blur()
          onChange?.(newItem.value)
          setInputValue(newItem.label)
        }
      },
    })

  const handleClear = () => {
    setInputValue('')
    onChange?.('')
    inputRef.current?.focus()
  }

  const handleBlur = (e: React.FocusEvent) => {
    if (!triggerRef.current?.contains(e.relatedTarget as Node)) {
      onBlur?.()
      if (selectedItem) setInputValue(selectedItem.label)
    }
  }

  const isPlaceholder = !selectedItem && !inputValue
  const showClearButton = inputValue && !isDisabled

  return (
    <DropdownWrapper
      className={className}
      label={label}
      helpText={helpText}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      isPlaceholder={isPlaceholder}
      isOpen={isOpen}
      labelProps={getLabelProps()}
    >
      <SearchInputWrapper
        ref={triggerRef}
        isOpen={isOpen}
        invalid={isInvalid}
        disabled={isDisabled}
      >
        <SearchInput
          {...getInputProps({ ref: inputRef, disabled: isDisabled, onBlur: handleBlur })}
          placeholder={placeholder}
        />
        <IconWrapper $rotate={false}>
          {showClearButton ? (
            <ClearButton type='button' onClick={handleClear} aria-label='Clear search'>
              <Icon name='SolidXClose' size='1.6rem' />
            </ClearButton>
          ) : (
            <Icon name='LineSearchSm' size='1.6rem' />
          )}
        </IconWrapper>
      </SearchInputWrapper>
      <DropdownMenu
        isOpen={isOpen && filteredOptions.length > 0}
        triggerRef={triggerRef}
        menuProps={getMenuProps({}, { suppressRefError: true })}
        options={filteredOptions}
        renderItem={(option, index) => (
          <StandardMenuItem
            option={option}
            itemProps={getItemProps({ item: option, index })}
            isSelected={selectedItem?.value === option.value}
            isHighlighted={highlightedIndex === index}
          />
        )}
      />
    </DropdownWrapper>
  )
}

// ======================
// Multi Select Dropdown
// ======================

const MultiSelectDropdown = ({
  id,
  className,
  label,
  helpText,
  placeholder = 'Select options',
  options,
  isInvalid = false,
  isDisabled = false,
  value = [],
  onChange,
  onBlur,
}: Omit<MultiSelectProps, 'multiple'>) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const selectedItems = useSelectedItems(options, value)

  const { getDropdownProps } = useMultipleSelection({
    selectedItems,
    onSelectedItemsChange: ({ selectedItems: newItems }) =>
      onChange?.(newItems?.map(item => item.value) ?? []),
  })

  const {
    isOpen,
    highlightedIndex,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
  } = useSelect({
    id,
    items: options,
    itemToString: item => item?.label ?? '',
    selectedItem: null,
    stateReducer: (state, { changes, type }) => {
      if (
        type === useSelect.stateChangeTypes.ToggleButtonKeyDownEnter ||
        type === useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton ||
        type === useSelect.stateChangeTypes.ItemClick
      ) {
        return {
          ...changes,
          isOpen: true,
          highlightedIndex: state.highlightedIndex ?? changes.highlightedIndex,
        }
      }

      if (type === useSelect.stateChangeTypes.ToggleButtonBlur) {
        return {
          ...changes,
          selectedItem: null,
        }
      }

      return changes
    },
    onSelectedItemChange: ({ selectedItem: clickedItem }) => {
      if (!clickedItem) return
      const isSelected = value.includes(clickedItem.value)
      onChange?.(
        isSelected ? value.filter(v => v !== clickedItem.value) : [...value, clickedItem.value]
      )
    },
    onIsOpenChange: ({ isOpen: newIsOpen }) => {
      if (!newIsOpen) onBlur?.()
    },
  })

  const displayValue =
    selectedItems.length > 0 ? selectedItems.map(item => item.label).join(', ') : null
  const isPlaceholder = !displayValue

  return (
    <DropdownWrapper
      className={className}
      label={label}
      helpText={helpText}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      isPlaceholder={isPlaceholder}
      isOpen={isOpen}
      labelProps={getLabelProps()}
    >
      <DropdownTriggerButton
        triggerRef={triggerRef}
        displayText={displayValue || placeholder}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        isPlaceholder={isPlaceholder}
        isOpen={isOpen}
        buttonProps={getToggleButtonProps(getDropdownProps({ preventKeyAction: isOpen }))}
      />
      <DropdownMenu
        isOpen={isOpen}
        triggerRef={triggerRef}
        menuProps={getMenuProps({}, { suppressRefError: true })}
        options={options}
        renderItem={(option, index) => {
          const isSelected = value.includes(option.value)
          return (
            <MenuItem
              {...getItemProps({ item: option, index })}
              isHighlighted={highlightedIndex === index}
              isSelected={isSelected}
              showSelectedIcon={false}
            >
              <Checkbox
                name={`${id}-option-${option.value}`}
                label={option.label}
                checked={isSelected}
                readOnly
                tabIndex={-1}
              />
            </MenuItem>
          )
        }}
      />
    </DropdownWrapper>
  )
}
